import type { Language } from './translations';

export const APP_LOCALES = ['en', 'pl'] as const;
export type AppLocale = Language;

export function isAppLocale(value: string): value is AppLocale {
  return value === 'en' || value === 'pl';
}

export function getLocaleFromPathname(pathname: string): AppLocale | null {
  const segment = pathname.split('/').filter(Boolean)[0];
  return segment && isAppLocale(segment) ? segment : null;
}

export function stripLocaleFromPathname(pathname: string): string {
  const locale = getLocaleFromPathname(pathname);
  if (!locale) {
    return pathname || '/';
  }

  const stripped = pathname.replace(new RegExp(`^/${locale}(?=/|$)`), '') || '/';
  return stripped.startsWith('/') ? stripped : `/${stripped}`;
}

export function getAppPathname(pathname: string): string {
  return stripLocaleFromPathname(pathname);
}

export function matchAppPath(pathname: string, prefix: string): boolean {
  const appPath = getAppPathname(pathname);
  return appPath === prefix || appPath.startsWith(`${prefix}/`);
}

export function withLocalePath(path: string, locale: AppLocale): string {
  const hashIndex = path.indexOf('#');
  const hash = hashIndex >= 0 ? path.slice(hashIndex) : '';
  const withoutHash = hashIndex >= 0 ? path.slice(0, hashIndex) : path;

  const queryIndex = withoutHash.indexOf('?');
  const pathnamePart = queryIndex >= 0 ? withoutHash.slice(0, queryIndex) : withoutHash;
  const queryPart = queryIndex >= 0 ? withoutHash.slice(queryIndex) : '';

  const normalizedPath = pathnamePart.startsWith('/') ? pathnamePart : `/${pathnamePart}`;
  const appPath = stripLocaleFromPathname(normalizedPath);
  const localizedPath = appPath === '/' ? `/${locale}` : `/${locale}${appPath}`;

  return `${localizedPath}${queryPart}${hash}`;
}

export function switchLocaleInPath(pathname: string, search: string, hash: string, locale: AppLocale): string {
  return withLocalePath(`${stripLocaleFromPathname(pathname)}${search}${hash}`, locale);
}
